// A section that re-fetches its own server-rendered HTML on a timer. Used by the dashboard's
// "סליקות אחרונות" board so admins can watch payments move without reloading the page.
//
// The markup is rendered server-side on first paint and this only swaps it, so the board still
// works with JS off — it just stops refreshing.
import type { Alpine } from "alpinejs";

/// The Alpine magics this component touches (not part of the returned data object).
type Refs = { $refs: Record<string, HTMLElement> };

export function registerLiveFeed(alpine: Alpine): void {
  // Alpine passes x-data arguments through as unknown[], so narrow them here.
  alpine.data("liveFeed", (...args: unknown[]) => {
    const url = args[0] as string;
    const intervalMs = (args[1] as number | undefined) ?? 15000;

    return {
      loading: false,
      failed: false,
      updatedAt: null as Date | null,
      timer: 0,
      onVisibility: () => {},

      init(this: any) {
        this.timer = window.setInterval(() => this.refresh(), intervalMs);
        // A hidden tab shouldn't keep polling; catch up the moment it comes back.
        this.onVisibility = () => {
          if (!document.hidden) this.refresh();
        };
        document.addEventListener("visibilitychange", this.onVisibility);
      },

      destroy(this: any) {
        window.clearInterval(this.timer);
        document.removeEventListener("visibilitychange", this.onVisibility);
      },

      async refresh(this: Refs & { loading: boolean; failed: boolean; updatedAt: Date | null }) {
        // Skip while backgrounded or while a previous fetch is still in flight — a slow response
        // must not queue up overlapping swaps.
        if (document.hidden || this.loading) return;

        this.loading = true;
        try {
          const res = await fetch(url, {
            headers: { "X-Requested-With": "XMLHttpRequest" },
            credentials: "same-origin",
          });
          // A session timeout answers the fetch with the login page — HTML that would replace the
          // board with a login form. Reload instead, so the normal auth redirect happens.
          if (res.redirected || res.status === 401 || res.status === 403) {
            window.location.reload();
            return;
          }
          if (!res.ok) throw new Error(`HTTP ${res.status}`);

          this.$refs.feed.innerHTML = await res.text();
          this.updatedAt = new Date();
          this.failed = false;
        } catch {
          // Keep the last good rows on screen; the next tick retries.
          this.failed = true;
        } finally {
          this.loading = false;
        }
      },

      get updatedLabel(): string {
        if (!this.updatedAt) return "";
        const t = this.updatedAt.toLocaleTimeString("he-IL", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
        return `עודכן ${t}`;
      },
    };
  });
}
